import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import ReportForm from "@/pages/ReportForm";
import { Button } from "@/components/ui/button";
import { Download, Settings2, CreditCard, Check, ArrowRight, ShieldCheck, Smartphone } from "lucide-react";

const DEFAULT_LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_agent-hub-375/artifacts/ijuy74i5_LOGO%20DOPOPAY.webp";

export default function Landing({ agent = null }) {
  const [settings, setSettings] = useState({ time_slots: [], logo_url: DEFAULT_LOGO });

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const scrollToForm = () => document.getElementById("form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#111111]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <img src={settings.logo_url || DEFAULT_LOGO} alt="Dopopay" className="h-7 w-auto" data-testid="header-logo" />
          <Button onClick={scrollToForm} data-testid="header-cta"
            className="rounded-full bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold h-10 px-5 transition-transform duration-200 hover:scale-105">
            Richiedi l'attivazione
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-[#111111] text-white overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#FFCC00]/20 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-5 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            {agent && (
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm mb-5" data-testid="agent-badge">
                <ShieldCheck className="w-4 h-4 text-[#FFCC00]" /> Segnalazione a cura di {agent.name} {agent.surname}
              </div>
            )}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Offri ai tuoi clienti il pagamento a rate direttamente dal POS <span className="text-[#FFCC00]">myPOS Ultra</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-xl">
              Con Dopopay puoi accettare pagamenti dilazionati tramite Klarna e Scalapay direttamente dal tuo POS myPOS Ultra, scaricando semplicemente l'app dal Play Store myPOS.
            </p>
            <div className="mt-8 inline-flex items-baseline gap-2 bg-white/5 border border-white/10 rounded-2xl px-6 py-4">
              <span className="font-display text-4xl font-bold text-[#FFCC00]">199 €</span>
              <span className="text-white/70">+ IVA una tantum</span>
            </div>
            <div className="mt-8">
              <Button onClick={scrollToForm} data-testid="hero-cta"
                className="rounded-full bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold h-14 px-8 text-base transition-transform duration-200 hover:-translate-y-0.5">
                Richiedi l'attivazione <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          </div>
          <div className="animate-fade-up lg:block" style={{ animationDelay: "0.15s" }} data-testid="hero-conditions">
            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-sm">
              <p className="text-sm font-semibold text-white/80 mb-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#FFCC00]" /> Condizioni economiche
              </p>
              <div className="grid grid-cols-2 gap-3">
                <MiniPrice title="Klarna" commission="4,99%" min="50 €" max="1.500 €" rate="fino a 3 rate" fee="0,50 € / transazione" />
                <MiniPrice title="Scalapay" commission="5,99%" min="50 €" max="4.999 €" rate="fino a 4 rate" fee="0,50 € / transazione" highlight />
              </div>
              <p className="text-xs text-white/50 mt-4 leading-relaxed">
                L'approvazione del pagamento è soggetta alle verifiche e alle condizioni previste dal relativo provider di pagamento.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* myPOS Ultra integration + Come funziona */}
      <section className="py-20 md:py-28 bg-[#111111] text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1 flex justify-center">
            <div className="absolute -inset-4 bg-[#FFCC00]/10 rounded-3xl blur-2xl" />
            <img src="/mypos-ultra.png"
              alt="POS myPOS Ultra con Dopopay" className="relative w-full max-w-sm object-contain drop-shadow-2xl" data-testid="mypos-image" />
          </div>
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm mb-5">
              <Smartphone className="w-4 h-4 text-[#FFCC00]" /> Integrazione nativa myPOS Ultra
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              Come funziona, <span className="text-[#FFCC00]">direttamente dal POS</span>
            </h2>
            <p className="mt-4 text-white/70 leading-relaxed">
              Tre passaggi semplici: nessun PC, tablet o smartphone aggiuntivo.
            </p>
            <div className="mt-8 space-y-5">
              {[
                { icon: Download, n: "1", t: "Scarica l'app", d: "Scarica l'app Dopopay direttamente dal Play Store disponibile sul POS myPOS Ultra." },
                { icon: Settings2, n: "2", t: "Configura il servizio", d: "Un nostro operatore contatterà il cliente per completare l'attivazione e la configurazione." },
                { icon: CreditCard, n: "3", t: "Accetta pagamenti rateali", d: "Il cliente potrà proporre ai propri consumatori il pagamento in 3 o 4 rate direttamente dal POS." },
              ].map((s) => (
                <div key={s.n} className="flex items-start gap-4">
                  <div className="relative shrink-0">
                    <div className="w-11 h-11 rounded-xl bg-[#FFCC00] flex items-center justify-center">
                      <s.icon className="w-5 h-5 text-black" />
                    </div>
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white text-black text-xs font-bold flex items-center justify-center">{s.n}</span>
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg">{s.t}</h3>
                    <p className="text-white/60 text-sm mt-1 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Costo attivazione */}
      <ReportForm agent={agent} timeSlots={settings.time_slots} />

      {/* Footer */}
      <footer className="bg-[#111111] text-white/60 py-12">
        <div className="max-w-6xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <img src={settings.logo_url || DEFAULT_LOGO} alt="Dopopay" className="h-6 w-auto" />
          <p className="text-sm">Facile, veloce, flessibile · © {new Date().getFullYear()} Dopopay</p>
          <a href="/admin" data-testid="footer-admin-link"
            className="text-sm font-semibold text-white bg-[#0052FF] hover:bg-[#0043cc] rounded-full px-4 py-2 transition-colors duration-200">
            Area operatori
          </a>
        </div>
      </footer>
    </div>
  );
}

function MiniPrice({ title, commission, min, max, rate, fee, highlight }) {
  return (
    <div data-testid={`price-card-${title.toLowerCase()}`}
      className={`rounded-2xl p-4 border ${highlight ? "border-[#FFCC00] bg-[#FFCC00]/10" : "border-white/15 bg-white/5"}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-bold text-white">{title}</h3>
        {highlight && <span className="text-[10px] bg-[#FFCC00] text-black font-semibold rounded-full px-2 py-0.5">Top</span>}
      </div>
      <ul className="space-y-2 text-sm">
        {[["Commissione", commission], ["Costo fisso", fee], ["Min", min], ["Max", max], ["Rate", rate]].map(([k, v]) => (
          <li key={k} className="flex items-center justify-between">
            <span className="text-white/50 text-xs">{k}</span>
            <span className="font-semibold text-white">{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
